import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Checkbox,
  Button,
  IconButton,
  Collapse,
  Box,
  Tooltip
} from '@mui/material'
import { ExpandLess, ExpandMore, Delete } from '@mui/icons-material'
import React, { Fragment, useEffect, useState } from 'react'
import {
  PreInboundReason,
  PreInboundReasonsGrid,
  WorkOrderItems
} from './types'
import { useStyles } from './styles'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../_EOS/Modal'
import { AddReason } from './components/addReasons'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { getPreInboundReason } from './api'
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos'

interface WorkOrderDetailsTableProps {
  data: WorkOrderItems[]
  handleValueChange: (quantity: number, workOrderDetail: WorkOrderItems) => void
  handleCheckChange: (id: number, checked: boolean) => void
  listReasons: PreInboundReasonsGrid[]
  setListReasons: React.Dispatch<React.SetStateAction<PreInboundReasonsGrid[]>>
}

export const WorkOrderDetailsTable: React.FC<WorkOrderDetailsTableProps> = ({
  data,
  handleValueChange,
  handleCheckChange,
  listReasons,
  setListReasons
}) => {
  const classes = useStyles()
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const { state, callApi: callApiPreInboundReason } =
    useApi<ApiResponse<PreInboundReason>>()

  const { size, isOpen, title, openModal, closeModal, extendedProps } =
    useModal<WorkOrderItems>('Agregar motivos')

  const handleExpandClick = (orderDetailId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(orderDetailId)) {
        newSet.delete(orderDetailId)
      } else {
        newSet.add(orderDetailId)
      }
      return newSet
    })
  }

  const isError = (item: WorkOrderItems): boolean => {
    return (
      item.expectedToReturnQuantity >
        item.dispatchedQuantity - item.returnedQuantity ||
      item.expectedToReturnQuantity <= 0
    )
  }

  const getErrorText = (item: WorkOrderItems): string | undefined => {
    if (
      item.expectedToReturnQuantity >
      item.dispatchedQuantity - item.returnedQuantity
    ) {
      return 'Cantidad retornada debe ser menor o igual a cantidad despachada'
    }

    if (item.expectedToReturnQuantity <= 0) {
      return 'Cantidad retornada debe ser mayor a 0'
    }

    return undefined
  }

  const RequestGetPreInboundReason = () => {
    callApiPreInboundReason(getPreInboundReason()).catch((error) => {
      console.error(JSON.stringify(error))
    })
  }

  useEffect(() => {
    RequestGetPreInboundReason()
  }, [])

  const handleDeleteReason = (
    preInboundReasonId: number,
    orderDetailId: number
  ) => {
    setListReasons((prevList) =>
      prevList.filter(
        (reason) =>
          !(
            reason.preInboundReasonId === preInboundReasonId &&
            reason.orderDetailId === orderDetailId
          )
      )
    )
  }
  // Filtrar razones para el orderDetailId específico
  const getReasonsForDetail = (
    orderDetailId: number
  ): PreInboundReasonsGrid[] => {
    return listReasons.filter(
      (reason) => reason.orderDetailId === orderDetailId
    )
  }

  // función para habilitar el botón agregar motivos solo si cumple condición
  const isValidQuantity = (item: WorkOrderItems): boolean => {
    const availableQuantity = item.dispatchedQuantity - item.returnedQuantity
    return (
      item.expectedToReturnQuantity > 0 &&
      item.expectedToReturnQuantity <= availableQuantity
    )
  }

  const getTotalReasonQuantity = (orderDetailId: number): number => {
    const reasonsForDetail = getReasonsForDetail(orderDetailId)
    return reasonsForDetail.reduce(
      (total, reason) => total + reason.quantity,
      0
    )
  }

  const isAddReasonDisabled = (item: WorkOrderItems): boolean => {
    return (
      !item.checked ||
      !isValidQuantity(item) ||
      getTotalReasonQuantity(item.orderDetailId) >=
        item.expectedToReturnQuantity
    )
  }

  return (
    <Fragment>
      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align='center'></TableCell>
              <TableCell align='center'>SKU</TableCell>
              <TableCell align='center'>EAN13</TableCell>
              <TableCell align='center'>ID Externo</TableCell>
              <TableCell align='center'>Nombre Producto</TableCell>
              <TableCell align='center'>UN Despachada</TableCell>
              <TableCell align='center'>UN Disponible</TableCell>
              <TableCell align='center'>UN Devuelta</TableCell>
              <TableCell align='center'>Motivo</TableCell>
              <TableCell align='center'></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .sort((a, b) => a.workOrderDetailId - b.workOrderDetailId)
              .map((item) => {
                const isExpanded = expandedRows.has(item.orderDetailId)
                const reasonsForDetail = getReasonsForDetail(item.orderDetailId)

                return (
                  <React.Fragment key={item.workOrderDetailId}>
                    <TableRow>
                      <TableCell align='center'>
                        <Checkbox
                          checked={item.checked || false}
                          onChange={(e) =>
                            handleCheckChange(
                              item.workOrderDetailId,
                              e.target.checked
                            )
                          }
                        />
                      </TableCell>
                      <TableCell align='center'>{item.sku}</TableCell>
                      <TableCell align='center'>{item.ean}</TableCell>
                      <TableCell align='center'>
                        {item.externalOrderId}
                      </TableCell>
                      <TableCell align='center'>{item.productName}</TableCell>
                      <TableCell align='center'>
                        {item.dispatchedQuantity}
                      </TableCell>
                      <TableCell align='center'>
                        {item.dispatchedQuantity - item.returnedQuantity}
                      </TableCell>
                      <TableCell align='center' style={{ maxWidth: '30px' }}>
                        <TextField
                          disabled={!item.checked}
                          error={isError(item)}
                          helperText={getErrorText(item)}
                          variant='outlined'
                          type='number'
                          value={item.expectedToReturnQuantity}
                          onChange={(e) =>
                            handleValueChange(parseInt(e.target.value), item)
                          }
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <Tooltip title='Ingresar Motivos' aria-label='edit'>
                          <IconButton
                            size='small'
                            onClick={() => openModal('md', item)}
                            aria-label='opciones de prioridad'
                            disabled={isAddReasonDisabled(item)}
                          >
                            <AddToPhotosIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align='center'>
                        <IconButton
                          aria-label='expand row'
                          size='small'
                          disabled={reasonsForDetail.length === 0}
                          onClick={() => handleExpandClick(item.orderDetailId)}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={10}
                      >
                        <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                          <Box margin={2}>
                            {reasonsForDetail.length > 0 ? (
                              <TableContainer>
                                <Table size='small'>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell align='center'>
                                        Cantidad
                                      </TableCell>
                                      <TableCell align='center'>
                                        Motivo
                                      </TableCell>
                                      <TableCell align='center'>
                                        Acción
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {reasonsForDetail.map((reason, index) => (
                                      <TableRow
                                        key={`${reason.orderDetailId}-${reason.preInboundReasonId}-${index}`}
                                      >
                                        <TableCell align='center'>
                                          {reason.quantity}
                                        </TableCell>
                                        <TableCell align='center'>
                                          {reason.preInboundReasonName}
                                        </TableCell>
                                        <TableCell align='center'>
                                          <IconButton
                                            size='small'
                                            color='secondary'
                                            onClick={() =>
                                              handleDeleteReason(
                                                reason.preInboundReasonId,
                                                reason.orderDetailId
                                              )
                                            }
                                          >
                                            <Delete />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Box textAlign='center' py={2}>
                                <p>No hay motivos agregados</p>
                              </Box>
                            )}

                            {reasonsForDetail.length >= 3 && (
                              <Box mt={2} textAlign='center'>
                                <Button
                                  variant='text'
                                  color='primary'
                                  size='small'
                                  onClick={() => alert('información adicional')}
                                >
                                  Más información
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal open={isOpen} title={title} onClose={closeModal} maxWidth={size}>
        <AddReason
          listReasons={listReasons}
          setListReasons={setListReasons}
          onCloseModal={closeModal}
          preInboundReasonlist={state.data?.items}
          workOrderItems={extendedProps}
          handleSnackbar={(message, success) => console.log(message, success)}
        />
      </Modal>
    </Fragment>
  )
}
